import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { usePrompt } from '../providers/PromptProvider';
import { ArrowPathIcon, ChartBarIcon, ChevronDownIcon, ChevronRightIcon, ClipboardDocumentIcon, CommandLineIcon, EllipsisVerticalIcon, EyeIcon, EyeSlashIcon, InformationCircleIcon, PencilSquareIcon, PlayIcon, Square3Stack3DIcon, StopIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Dropdown } from '../components/ui';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatBytes, formatCurrency, formatCurrencyPrecise, formatDateTime } from '../lib/format';

interface KeyValue { key: string; value: string; }
interface Port { host: string; container: string; }
interface VolumePair { host: string; container: string; }
interface DeployConfig {
  mode: string;
  replicas: string;
  placementConstraints: string[];
  labels: KeyValue[];
}
interface ServiceForm {
  name: string;
  image: string;
  ports: Port[];
  environment: KeyValue[];
  volumes: VolumePair[];
  networks: string[];
  labels: KeyValue[];
  deploy: DeployConfig;
}
interface StatsPoint {
  time: Date;
  cpuPercent: number;
  memUsageMB: number;
  memPercent: number;
  netRxBytes: number;
  netTxBytes: number;
  ioReadBytes: number;
  ioWriteBytes: number;
  pids: number;
}

const Sparkline: React.FC<{ data: number[]; color: string; fillColor: string; label?: string }> = ({ data, color, fillColor, label }) => {
  if (data.length < 2) {
    return (
      <div className="h-16 w-full flex items-center justify-center">
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    );
  }
  const max = Math.max(...data, 0.001);
  const min = Math.min(...data);
  const range = max - min || 1;
  const W = 300, H = 64, P = 4;
  const pts = data.map((v, i) => {
    const x = P + (i / (data.length - 1)) * (W - 2 * P);
    const y = (H - P) - ((v - min) / range) * (H - 2 * P);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M ${pts.join(' L ')}`;
  const fillPath = `${linePath} L ${(W - P).toFixed(1)},${H} L ${P},${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
      <path d={fillPath} fill={fillColor} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

const fmtBytes = (b: number): string => {
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(2)} GB`;
  if (b >= 1048576) return `${(b / 1048576).toFixed(2)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(2)} KB`;
  return `${b} B`;
};

interface NetworkConfig { driver?: string; external?: boolean; }
interface VolumeConfig { driver?: string; external?: boolean; }
interface StackForm {
  name: string;
  services: ServiceForm[];
  globalNetworks: { [name: string]: NetworkConfig };
  globalVolumes: { [name: string]: VolumeConfig };
}
interface Stack {
  Id: number;
  Name: string;
  Status: number;
  type?: string;
  price_mtd?: number;
  price_currency?: string;
}

interface StackMetricSample {
  id: number;
  stack_name: string;
  sampled_at: string;
  is_running: boolean;
  container_count: number;
  cpu_vcpu: number;
  mem_bytes: number;
  net_rx_bytes: number;
  net_tx_bytes: number;
  io_read_bytes: number;
  io_write_bytes: number;
}

interface PriceBreakdown { base: number; cpu: number; mem: number; }

interface PricingRates {
  Currency: string;
  BasePerHour: number;
  CPUPerVCPUHour: number;
  MemPerGBHour: number;
}

interface StackDetails {
  id: number;
  name: string;
  stack_name: string;
  type: string;
  status: number;
  created_at: string;
  updated_at: string;
  service_count: number;
  network_count: number;
  volume_count: number;
  uptime_seconds: number;
  uptime_percent: number;
  avg_cpu_vcpu: number;
  avg_mem_bytes: number;
  peak_cpu_vcpu: number;
  peak_mem_bytes: number;
  container_count: number;
  currency: string;
  price_lifetime: PriceBreakdown;
  price_mtd: PriceBreakdown;
  rates: PricingRates;
  samples: StackMetricSample[];
}

interface Task {
  ID: string;
  Slot: number;
  NodeID: string;
  Spec?: { ContainerSpec?: { Image?: string } };
  Status: {
    State: string;
    Timestamp: string;
    ContainerStatus?: { ContainerID?: string };
  };
  UpdatedAt: string;
}

interface ProgressStep {
  id: number;
  label: string;
  duration: number;
  status: 'pending' | 'running' | 'completed' | 'error';
  elapsed?: number;
}

type ModalMode = 'create' | 'edit' | 'view';

const emptyService = (): ServiceForm => ({
  name: '',
  image: '',
  ports: [{ host: '', container: '' }],
  environment: [{ key: '', value: '' }],
  volumes: [{ host: '', container: '' }],
  networks: ['traefik-public'],
  labels: [{ key: '', value: '' }],
  deploy: {
    mode: 'replicated',
    replicas: '1',
    placementConstraints: [''],
    labels: [{ key: '', value: '' }],
  },
});

const emptyForm = (): StackForm => ({
  name: '',
  services: [emptyService()],
  globalNetworks: {},
  globalVolumes: {},
});

const Containers: React.FC = () => {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const location = useLocation();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const { prompt } = usePrompt();
  const [stacks, setStacks] = useState<Stack[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [selectedStack, setSelectedStack] = useState<Stack | null>(null);
  const [stackForm, setStackForm] = useState<StackForm>(emptyForm());
  const [formError, setFormError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [showYaml, setShowYaml] = useState<boolean>(false);
  // When the user enters YAML mode and edits, this becomes the source of truth
  // for submission (sent as composeContent). Reset when leaving the modal.
  const [yamlDraft, setYamlDraft] = useState<string>('');
  const [yamlDirty, setYamlDirty] = useState<boolean>(false);

  // Public-API token state (start/stop/restart over HTTP without Keycloak).
  const [apiToken, setApiToken] = useState<string>('');
  const [tokenRevealed, setTokenRevealed] = useState<boolean>(false);
  const [regeneratingToken, setRegeneratingToken] = useState<boolean>(false);
  const [apiCodeLang, setApiCodeLang] = useState<'curl' | 'python' | 'javascript'>('curl');
  const [expandedStackId, setExpandedStackId] = useState<number | null>(null);
  const [stackTasks, setStackTasks] = useState<{ [stackId: number]: Task[] | null }>({});
  const [tasksLoading, setTasksLoading] = useState<{ [stackId: number]: boolean }>({});
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [progressCurrentStep, setProgressCurrentStep] = useState(-1);
  const [progressTitle, setProgressTitle] = useState('');
  const [liveTick, setLiveTick] = useState(0);
  const progressStepStartRef = useRef<number>(0);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsStackId, setLogsStackId] = useState<number | null>(null);
  const [logsContainerId, setLogsContainerId] = useState('');
  const [logsTail, setLogsTail] = useState('100');
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const logsScrollRef = useRef<HTMLPreElement>(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsStackId, setStatsStackId] = useState<number | null>(null);
  const [statsContainerId, setStatsContainerId] = useState('');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsHistory, setStatsHistory] = useState<StatsPoint[]>([]);
  const [statsName, setStatsName] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [details, setDetails] = useState<StackDetails | null>(null);

  const handleOpenDetails = async (stack: Stack) => {
    setDetailsOpen(true);
    setDetails(null);
    setDetailsError(null);
    setDetailsLoading(true);
    try {
      const res = await axios.get<StackDetails>(`${API_URL}/stacks/${stack.Id}/details`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      setDetails(res.data);
    } catch (err: any) {
      setDetailsError(err?.response?.data?.error || 'Failed to load stack details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatDuration = (totalSeconds: number): string => {
    if (!totalSeconds || totalSeconds < 0) return '0m';
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  useEffect(() => {
    fetchStacks();
    fetchApiToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchApiToken = async () => {
    if (!keycloak?.token) return;
    try {
      const res = await axios.get(`${API_URL}/stacks/token`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      setApiToken(res.data?.token || '');
    } catch {
      // Soft-fail; the token card will show an empty state and a regenerate button.
    }
  };

  const handleRegenerateApiToken = async () => {
    if (!keycloak?.token) return;
    const ok = await confirm({
      title: 'Regenerate API token',
      message: 'The current token will stop working immediately. Anywhere it is in use must be updated.',
      confirmLabel: 'Regenerate',
      variant: 'danger',
    });
    if (!ok) return;
    setRegeneratingToken(true);
    try {
      const res = await axios.post(`${API_URL}/stacks/token/regenerate`, {}, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      setApiToken(res.data?.token || '');
      setTokenRevealed(true);
      toast('API token regenerated', 'success');
    } catch (err: any) {
      toast(err?.response?.data?.error || 'Failed to regenerate token', 'error');
    } finally {
      setRegeneratingToken(false);
    }
  };

  const handleCopyApiToken = () => {
    if (!apiToken) return;
    navigator.clipboard.writeText(apiToken).then(
      () => toast('Token copied to clipboard', 'success'),
      () => toast('Failed to copy token', 'error'),
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stackIdParam = params.get('stackId');
    if (stackIdParam && stacks.length > 0) {
      const stack = stacks.find((s) => String(s.Id) === String(stackIdParam));
      if (stack) handleOpenEdit(stack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, stacks]);

  const fetchStacks = async () => {
    try {
      const response = await axios.get(`${API_URL}/stacks`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      setStacks(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stacks:', err);
      setError('Failed to load stacks');
      setLoading(false);
    }
  };

  const resetForm = () => { setStackForm(emptyForm()); setExpandedSections({}); };

  const STABLE_STATES = new Set(['running', 'failed', 'rejected', 'shutdown', 'complete', 'orphaned']);

  const fetchTasksForStack = async (id: number) => {
    setTasksLoading((p) => ({ ...p, [id]: true }));
    try {
      const response = await axios.get(`${API_URL}/stacks/${id}/tasks`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      setStackTasks((p) => ({ ...p, [id]: response.data || [] }));
    } catch (err) {
      setStackTasks((p) => ({ ...p, [id]: [] }));
    } finally {
      setTasksLoading((p) => ({ ...p, [id]: false }));
    }
  };

  // Auto-poll tasks while any task is in a transitional state
  useEffect(() => {
    if (expandedStackId === null) return;
    const tasks = stackTasks[expandedStackId];
    if (!tasks || tasks.length === 0) return;
    const hasTransitional = tasks.some((t) => !STABLE_STATES.has(t.Status?.State ?? ''));
    if (!hasTransitional) return;
    const timer = setInterval(() => fetchTasksForStack(expandedStackId), 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedStackId, stackTasks]);

  // Drive the live elapsed-time counter in the progress modal
  useEffect(() => {
    if (!showProgressModal || progressCurrentStep < 0) return;
    const timer = setInterval(() => setLiveTick((t) => t + 1), 100);
    return () => clearInterval(timer);
  }, [showProgressModal, progressCurrentStep]);

  // Poll container logs every 5s while the logs modal is open
  useEffect(() => {
    if (!logsOpen || !logsStackId || !logsContainerId) return;
    let cancelled = false;
    const doFetch = async () => {
      if (cancelled) return;
      setLogsLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/stacks/${logsStackId}/containers/${logsContainerId}/logs?tail=${logsTail}`,
          { headers: { Authorization: `Bearer ${keycloak?.token}` } }
        );
        if (!cancelled) setLogsContent(res.data?.logs || '');
      } catch { /* keep existing content */ } finally {
        if (!cancelled) setLogsLoading(false);
      }
    };
    doFetch();
    const timer = setInterval(doFetch, 5000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsOpen, logsStackId, logsContainerId, logsTail]);

  // Auto-scroll logs to bottom on every update
  useEffect(() => {
    if (logsScrollRef.current) {
      logsScrollRef.current.scrollTop = logsScrollRef.current.scrollHeight;
    }
  }, [logsContent]);

  // Poll container stats every 5s while the stats modal is open
  useEffect(() => {
    if (!statsOpen || !statsStackId || !statsContainerId) return;
    let cancelled = false;
    const doFetch = async () => {
      if (cancelled) return;
      setStatsLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/stacks/${statsStackId}/containers/${statsContainerId}/stats`,
          { headers: { Authorization: `Bearer ${keycloak?.token}` } }
        );
        if (!cancelled) {
          const d = res.data;
          const cpuDelta = (d.cpu_stats?.cpu_usage?.total_usage ?? 0) - (d.precpu_stats?.cpu_usage?.total_usage ?? 0);
          const sysDelta = (d.cpu_stats?.system_cpu_usage ?? 0) - (d.precpu_stats?.system_cpu_usage ?? 0);
          const cpuPercent = sysDelta > 0 ? (cpuDelta / sysDelta) * (d.cpu_stats?.online_cpus ?? 1) * 100 : 0;
          const memUsageMB = (d.memory_stats?.usage ?? 0) / 1048576;
          const memPercent = d.memory_stats?.limit > 0 ? (d.memory_stats.usage / d.memory_stats.limit) * 100 : 0;
          const nets: any[] = Object.values(d.networks ?? {});
          const netRxBytes = nets.reduce((a: number, n: any) => a + (n.rx_bytes ?? 0), 0);
          const netTxBytes = nets.reduce((a: number, n: any) => a + (n.tx_bytes ?? 0), 0);
          const blkio: any[] = d.blkio_stats?.io_service_bytes_recursive ?? [];
          const ioReadBytes = blkio.filter((e: any) => e.op === 'read').reduce((a: number, e: any) => a + (e.value ?? 0), 0);
          const ioWriteBytes = blkio.filter((e: any) => e.op === 'write').reduce((a: number, e: any) => a + (e.value ?? 0), 0);
          setStatsHistory((prev) => [...prev.slice(-19), {
            time: new Date(),
            cpuPercent,
            memUsageMB,
            memPercent,
            netRxBytes,
            netTxBytes,
            ioReadBytes,
            ioWriteBytes,
            pids: d.pids_stats?.current ?? 0,
          }]);
          if (d.name) setStatsName(d.name);
        }
      } catch { /* keep existing */ } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    doFetch();
    const timer = setInterval(doFetch, 5000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsOpen, statsStackId, statsContainerId]);

  const refreshTasksIfExpanded = (stackId: number) => {
    setStackTasks((p) => { const n = { ...p }; delete n[stackId]; return n; });
    if (expandedStackId === stackId) fetchTasksForStack(stackId);
  };

  const runProgressSteps = async (
    title: string,
    steps: { label: string; duration: number }[],
    apiCall: () => Promise<void>,
    onDone: () => void,
    onError?: (err: any) => void
  ) => {
    setProgressTitle(title);
    setProgressSteps(steps.map((s, id) => ({ id, ...s, status: 'pending' as const })));
    setProgressCurrentStep(0);
    setShowProgressModal(true);
    let failedAt = 0;
    try {
      for (let i = 0; i < steps.length; i++) {
        failedAt = i;
        setProgressCurrentStep(i);
        progressStepStartRef.current = Date.now();
        if (i < steps.length - 1) {
          await new Promise<void>((res) => setTimeout(res, steps[i].duration));
        } else {
          await apiCall();
        }
        const elapsed = Date.now() - progressStepStartRef.current;
        setProgressSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, status: 'completed', elapsed } : s));
      }
      setProgressCurrentStep(-1);
      setTimeout(() => { setShowProgressModal(false); onDone(); }, 600);
    } catch (err: any) {
      setProgressSteps((prev) => prev.map((s, idx) => idx === failedAt ? { ...s, status: 'error' } : s));
      setProgressCurrentStep(-1);
      if (onError) {
        setTimeout(() => { setShowProgressModal(false); onError(err); }, 800);
      } else {
        setTimeout(() => {
          setShowProgressModal(false);
          toast('Operation failed: ' + (err.response?.data?.error || err.message), 'error');
        }, 1500);
      }
    }
  };

  const handleOpenLogs = (stackId: number, containerId: string) => {
    setLogsStackId(stackId);
    setLogsContainerId(containerId);
    setLogsContent('');
    setLogsTail('100');
    setLogsOpen(true);
  };

  const handleOpenStats = (stackId: number, containerId: string) => {
    setStatsStackId(stackId);
    setStatsContainerId(containerId);
    setStatsHistory([]);
    setStatsName('');
    setStatsOpen(true);
  };

  const handleToggleTasks = (stack: Stack) => {
    const id = stack.Id;
    if (expandedStackId === id) { setExpandedStackId(null); return; }
    setExpandedStackId(id);
    if (stackTasks[id] !== undefined) return;
    fetchTasksForStack(id);
  };

  const handleOpenCreate = () => { resetForm(); setModalMode('create'); setFormError(''); setShowYaml(false); setYamlDraft(''); setYamlDirty(false); setShowModal(true); };

  const handleOpenEdit = async (stack: Stack) => {
    setModalMode(stack.type === 'website' ? 'view' : 'edit');
    setSelectedStack(stack);
    setFormError('');
    setShowYaml(false);
    setYamlDraft('');
    setYamlDirty(false);
    setShowModal(true);
    try {
      const response = await axios.get(`${API_URL}/stacks/${stack.Id}`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      const stackData = response.data;
      if (stackData.parsed && stackData.parsed.services?.length > 0) {
        const services: ServiceForm[] = stackData.parsed.services.map((s: any) => ({
          name: s.name || '',
          image: s.image || '',
          ports: s.ports?.length > 0 ? s.ports : [{ host: '', container: '' }],
          environment: s.environment?.length > 0 ? s.environment : [{ key: '', value: '' }],
          volumes: s.volumes?.length > 0 ? s.volumes : [{ host: '', container: '' }],
          networks: s.networks?.length > 0 ? s.networks : ['traefik-public'],
          labels: s.labels?.length > 0 ? s.labels : [{ key: '', value: '' }],
          deploy: {
            mode: s.deploy?.mode || 'replicated',
            replicas: s.deploy?.replicas || '1',
            placementConstraints: s.deploy?.placementConstraints?.length > 0 ? s.deploy.placementConstraints : [''],
            labels: s.deploy?.labels?.length > 0 ? s.deploy.labels : [{ key: '', value: '' }],
          },
        }));
        setStackForm({
          name: stack.Name.split('-')[0],
          services,
          globalNetworks: stackData.parsed.globalNetworks || {},
          globalVolumes: stackData.parsed.globalVolumes || {},
        });
      } else {
        setStackForm({ name: stack.Name.split('-')[0], services: [emptyService()], globalNetworks: {}, globalVolumes: {} });
      }
    } catch (err) {
      console.error('Error fetching stack details:', err);
      setFormError('Failed to load stack details');
      setStackForm({ name: stack.Name.split('-')[0], services: [emptyService()], globalNetworks: {}, globalVolumes: {} });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const submitYaml = yamlDirty && yamlDraft.trim() !== '';
    const isEdit = modalMode === 'edit' && selectedStack;

    // Stack-name validation. In YAML edit mode for an existing stack the name
    // is taken from selectedStack and not editable here, so skip it. For
    // create mode the structured name field is still the source of truth.
    if (!isEdit) {
      if (!/^[a-z0-9-]+$/.test(stackForm.name)) {
        setFormError('Stack name must contain only lowercase letters, numbers, and hyphens');
        return;
      }
      // Docker Swarm limit: stack_name (with UUID appended) + _ + service_name must be ≤ 63 chars
      // portainer stack name = stackForm.name + "-" + uuid(36) → +37 chars
      // docker service = portainer_stack_name + "_" + service_name → +38 total overhead
      const maxCombined = 25; // 63 - 38
      if (stackForm.name.length > maxCombined) {
        setFormError(`Stack name is too long (max ${maxCombined} characters)`);
        return;
      }
    }

    // Structured-form per-service checks only apply when we're submitting the
    // structured form. In YAML mode the server-side sanitizer is the source
    // of truth and will return precise errors.
    if (!submitYaml) {
      const maxCombined = 25;
      if (stackForm.services.length === 0) { setFormError('At least one service is required'); return; }
      for (let i = 0; i < stackForm.services.length; i++) {
        if (!stackForm.services[i].name) { setFormError(`Service ${i + 1}: name is required`); return; }
        if (!stackForm.services[i].image) { setFormError(`Service ${i + 1}: image is required`); return; }
        const maxServiceName = maxCombined - stackForm.name.length;
        if (stackForm.services[i].name.length > maxServiceName) {
          setFormError(`Service ${i + 1}: name is too long. With stack name "${stackForm.name}", service name must be at most ${maxServiceName} characters.`);
          return;
        }
      }
    }
    setSubmitting(true);
    const cleanedServices = stackForm.services.map((service) => ({
      name: service.name,
      image: service.image,
      ports: service.ports.filter((p) => p.host && p.container),
      environment: service.environment.filter((e) => e.key && e.value),
      volumes: service.volumes.filter((v) => v.host && v.container),
      networks: service.networks.filter((n) => n),
      labels: service.labels.filter((l) => l.key && l.value),
      deploy: {
        mode: service.deploy.mode,
        replicas: service.deploy.mode === 'replicated' ? service.deploy.replicas : undefined,
        placementConstraints: service.deploy.placementConstraints.filter((c) => c),
        labels: service.deploy.labels.filter((l) => l.key && l.value),
      },
    }));
    const payload: Record<string, unknown> = {
      name: stackForm.name,
    };
    // If the user touched the YAML editor, ship the raw YAML and let the
    // server-side sanitizer be the source of truth. Otherwise send the
    // structured form as before.
    if (yamlDirty && yamlDraft.trim()) {
      payload.composeContent = yamlDraft;
    } else {
      payload.services = cleanedServices;
      payload.globalNetworks = stackForm.globalNetworks;
      payload.globalVolumes = stackForm.globalVolumes;
    }
    const steps = isEdit
      ? [{ label: 'Building stack configuration', duration: 800 }, { label: 'Updating the stack', duration: 0 }]
      : [{ label: 'Building stack configuration', duration: 1000 }, { label: 'Deploying stack', duration: 0 }];
    await runProgressSteps(
      isEdit ? 'Updating your stack' : 'Deploying your stack',
      steps,
      async () => {
        if (isEdit) {
          await axios.put(`${API_URL}/stacks/${selectedStack.Id}`, payload, {
            headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          });
        } else {
          await axios.post(`${API_URL}/stacks`, payload, {
            headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          });
        }
      },
      () => {
        setShowModal(false);
        resetForm();
        fetchStacks();
        toast(isEdit ? 'Stack updated successfully!' : 'Stack created successfully!', 'success');
        setSubmitting(false);
      },
      (err) => {
        setSubmitting(false);
        if (err.response?.status === 409) {
          setFormError('Stack name already exists. Please choose a different name.');
        } else {
          setFormError(err.response?.data?.error || 'Failed to save stack');
        }
      }
    );
  };

  const handleStartStack = (stackId: number) =>
    runProgressSteps(
      'Starting your stack',
      [{ label: 'Starting start signal', duration: 800 }, { label: 'Waiting for containers to start', duration: 0 }],
      () => axios.post(`${API_URL}/stacks/${stackId}/start`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => { fetchStacks(); refreshTasksIfExpanded(stackId); toast('Stack started successfully!', 'success'); }
    );

  const handleStopStack = (stackId: number) =>
    runProgressSteps(
      'Stopping your stack',
      [{ label: 'Sending stop signal', duration: 800 }, { label: 'Waiting for containers to stop', duration: 0 }],
      () => axios.post(`${API_URL}/stacks/${stackId}/stop`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => { fetchStacks(); refreshTasksIfExpanded(stackId); toast('Stack stopped successfully!', 'success'); }
    );

  const handleRestartStack = (stackId: number) =>
    runProgressSteps(
      'Restarting your stack',
      [
        { label: 'Stopping containers', duration: 800 },
        { label: 'Waiting for containers to fully stop', duration: 0 },
        { label: 'Starting containers again', duration: 0 },
      ],
      () => axios.post(`${API_URL}/stacks/${stackId}/restart`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` }, timeout: 120000 }),
      () => { fetchStacks(); refreshTasksIfExpanded(stackId); toast('Stack restarted successfully!', 'success'); }
    );

  const handleDeleteStack = async (stack: Stack) => {
    const ok = await confirm({ title: 'Delete stack', message: `Are you sure you want to delete stack "${stack.Name}"?`, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    runProgressSteps(
      'Deleting your stack',
      [{ label: 'Removing stack', duration: 0 }],
      () => axios.delete(`${API_URL}/stacks/${stack.Id}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => {
        if (expandedStackId === stack.Id) setExpandedStackId(null);
        setStackTasks((p) => { const n = { ...p }; delete n[stack.Id]; return n; });
        fetchStacks();
        toast('Stack deleted successfully!', 'success');
      }
    );
  };

  const addService = () => setStackForm((p) => ({ ...p, services: [...p.services, emptyService()] }));
  const removeService = (si: number) => setStackForm((p) => ({ ...p, services: p.services.filter((_, i) => i !== si) }));
  const updateService = (si: number, field: keyof ServiceForm, value: any) =>
    setStackForm((p) => { const s = [...p.services]; s[si] = { ...s[si], [field]: value }; return { ...p, services: s }; });
  const updateServiceDeploy = (si: number, field: keyof DeployConfig, value: any) =>
    setStackForm((p) => { const s = [...p.services]; s[si] = { ...s[si], deploy: { ...s[si].deploy, [field]: value } }; return { ...p, services: s }; });

  const addPort = (si: number) => updateService(si, 'ports', [...stackForm.services[si].ports, { host: '', container: '' }]);
  const removePort = (si: number, pi: number) => updateService(si, 'ports', stackForm.services[si].ports.filter((_, i) => i !== pi));
  const updatePort = (si: number, pi: number, f: 'host' | 'container', v: string) => {
    const a = [...stackForm.services[si].ports]; a[pi] = { ...a[pi], [f]: v }; updateService(si, 'ports', a);
  };

  const addEnvironment = (si: number) => {
    const next = [...stackForm.services[si].environment, { key: '', value: '' }];
    updateService(si, 'environment', next);
    // After the new row mounts, scroll it into view and focus the KEY input.
    requestAnimationFrame(() => {
      const target = document.querySelector<HTMLInputElement>(
        `[data-env-row="${si}-${next.length - 1}"] input[data-env-field="key"]`,
      );
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      }
    });
  };
  const removeEnvironment = (si: number, ei: number) => updateService(si, 'environment', stackForm.services[si].environment.filter((_, i) => i !== ei));
  const updateEnvironment = (si: number, ei: number, f: 'key' | 'value', v: string) => {
    const a = [...stackForm.services[si].environment]; a[ei] = { ...a[ei], [f]: v }; updateService(si, 'environment', a);
  };

  const addVolume = (si: number) => updateService(si, 'volumes', [...stackForm.services[si].volumes, { host: '', container: '' }]);
  const removeVolume = (si: number, vi: number) => updateService(si, 'volumes', stackForm.services[si].volumes.filter((_, i) => i !== vi));
  const updateVolume = (si: number, vi: number, f: 'host' | 'container', v: string) => {
    const a = [...stackForm.services[si].volumes]; a[vi] = { ...a[vi], [f]: v }; updateService(si, 'volumes', a);
  };

  const addNetwork = (si: number) => updateService(si, 'networks', [...stackForm.services[si].networks, '']);
  const removeNetwork = (si: number, ni: number) => updateService(si, 'networks', stackForm.services[si].networks.filter((_, i) => i !== ni));
  const updateNetwork = (si: number, ni: number, v: string) => {
    const a = [...stackForm.services[si].networks]; a[ni] = v; updateService(si, 'networks', a);
  };

  const addLabel = (si: number) => updateService(si, 'labels', [...stackForm.services[si].labels, { key: '', value: '' }]);
  const removeLabel = (si: number, li: number) => updateService(si, 'labels', stackForm.services[si].labels.filter((_, i) => i !== li));
  const updateLabel = (si: number, li: number, f: 'key' | 'value', v: string) => {
    const a = [...stackForm.services[si].labels]; a[li] = { ...a[li], [f]: v }; updateService(si, 'labels', a);
  };

  const addPlacementConstraint = (si: number) => updateServiceDeploy(si, 'placementConstraints', [...stackForm.services[si].deploy.placementConstraints, '']);
  const removePlacementConstraint = (si: number, ci: number) => updateServiceDeploy(si, 'placementConstraints', stackForm.services[si].deploy.placementConstraints.filter((_, i) => i !== ci));
  const updatePlacementConstraint = (si: number, ci: number, v: string) => {
    const a = [...stackForm.services[si].deploy.placementConstraints]; a[ci] = v; updateServiceDeploy(si, 'placementConstraints', a);
  };

  const addDeployLabel = (si: number) => updateServiceDeploy(si, 'labels', [...stackForm.services[si].deploy.labels, { key: '', value: '' }]);
  const removeDeployLabel = (si: number, li: number) => updateServiceDeploy(si, 'labels', stackForm.services[si].deploy.labels.filter((_, i) => i !== li));
  const updateDeployLabel = (si: number, li: number, f: 'key' | 'value', v: string) => {
    const a = [...stackForm.services[si].deploy.labels]; a[li] = { ...a[li], [f]: v }; updateServiceDeploy(si, 'labels', a);
  };

  const addGlobalNetwork = async () => {
    const n = await prompt({ message: 'Enter network name:', title: 'Add Network', placeholder: 'network-name' });
    if (n) setStackForm((p) => ({ ...p, globalNetworks: { ...p.globalNetworks, [n]: { external: false, driver: 'overlay' } } }));
  };
  const removeGlobalNetwork = (name: string) =>
    setStackForm((p) => { const n = { ...p.globalNetworks }; delete n[name]; return { ...p, globalNetworks: n }; });
  const updateGlobalNetwork = (name: string, field: keyof NetworkConfig, value: any) =>
    setStackForm((p) => ({ ...p, globalNetworks: { ...p.globalNetworks, [name]: { ...p.globalNetworks[name], [field]: value } } }));

  const addGlobalVolume = async () => {
    const n = await prompt({ message: 'Enter volume name:', title: 'Add Volume', placeholder: 'volume-name' });
    if (n) setStackForm((p) => ({ ...p, globalVolumes: { ...p.globalVolumes, [n]: { driver: 'local', external: false } } }));
  };
  const removeGlobalVolume = (name: string) =>
    setStackForm((p) => { const v = { ...p.globalVolumes }; delete v[name]; return { ...p, globalVolumes: v }; });
  const updateGlobalVolume = (name: string, field: keyof VolumeConfig, value: any) =>
    setStackForm((p) => ({ ...p, globalVolumes: { ...p.globalVolumes, [name]: { ...p.globalVolumes[name], [field]: value } } }));

  const toggleSection = (key: string) => setExpandedSections((p) => ({ ...p, [key]: !p[key] }));
  const isReadOnly = modalMode === 'view';

  const generateYaml = (form: StackForm): string => {
    const indent = (n: number) => '  '.repeat(n);
    const lines: string[] = ['version: "3.8"', ''];

    // services
    lines.push('services:');
    for (const svc of form.services) {
      if (!svc.name) continue;
      lines.push(`${indent(1)}${svc.name}:`);
      if (svc.image) lines.push(`${indent(2)}image: ${svc.image}`);

      const ports = svc.ports.filter(p => p.host && p.container);
      if (ports.length > 0) {
        lines.push(`${indent(2)}ports:`);
        for (const p of ports) lines.push(`${indent(3)}- "${p.host}:${p.container}"`);
      }

      const envs = svc.environment.filter(e => e.key && e.value);
      if (envs.length > 0) {
        lines.push(`${indent(2)}environment:`);
        for (const e of envs) lines.push(`${indent(3)}- ${e.key}=${e.value}`);
      }

      const vols = svc.volumes.filter(v => v.host && v.container);
      if (vols.length > 0) {
        lines.push(`${indent(2)}volumes:`);
        for (const v of vols) lines.push(`${indent(3)}- ${v.host}:${v.container}`);
      }

      const nets = svc.networks.filter(n => n);
      if (nets.length > 0) {
        lines.push(`${indent(2)}networks:`);
        for (const n of nets) lines.push(`${indent(3)}- ${n}`);
      }

      const lbls = svc.labels.filter(l => l.key && l.value);
      if (lbls.length > 0) {
        lines.push(`${indent(2)}labels:`);
        for (const l of lbls) lines.push(`${indent(3)}- "${l.key}=${l.value}"`);
      }

      // deploy
      const deployLabels = svc.deploy.labels.filter(l => l.key && l.value);
      const constraints = svc.deploy.placementConstraints.filter(c => c);
      const hasDeploy = svc.deploy.mode || deployLabels.length > 0 || constraints.length > 0;
      if (hasDeploy) {
        lines.push(`${indent(2)}deploy:`);
        lines.push(`${indent(3)}mode: ${svc.deploy.mode}`);
        if (svc.deploy.mode === 'replicated') {
          lines.push(`${indent(3)}replicas: ${svc.deploy.replicas}`);
        }
        if (constraints.length > 0) {
          lines.push(`${indent(3)}placement:`);
          lines.push(`${indent(4)}constraints:`);
          for (const c of constraints) lines.push(`${indent(5)}- "${c}"`);
        }
        if (deployLabels.length > 0) {
          lines.push(`${indent(3)}labels:`);
          for (const l of deployLabels) lines.push(`${indent(4)}- "${l.key}=${l.value}"`);
        }
      }
    }

    // networks
    const netKeys = Object.keys(form.globalNetworks);
    if (netKeys.length > 0) {
      lines.push('');
      lines.push('networks:');
      for (const name of netKeys) {
        const cfg = form.globalNetworks[name];
        lines.push(`${indent(1)}${name}:`);
        if (cfg.driver !== undefined) lines.push(`${indent(2)}driver: ${cfg.driver}`);
        if (cfg.external !== undefined) lines.push(`${indent(2)}external: ${cfg.external}`);
      }
    }

    // volumes
    const volKeys = Object.keys(form.globalVolumes);
    if (volKeys.length > 0) {
      lines.push('');
      lines.push('volumes:');
      for (const name of volKeys) {
        const cfg = form.globalVolumes[name];
        lines.push(`${indent(1)}${name}:`);
        if (cfg.driver !== undefined) lines.push(`${indent(2)}driver: ${cfg.driver}`);
        if (cfg.external !== undefined) lines.push(`${indent(2)}external: ${cfg.external}`);
      }
    }

    return lines.join('\n');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<Square3Stack3DIcon />}
          title="Containers"
          description="Manage your stacks."
          actions={
            <button
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary"
              disabled
            >
              New Stack
            </button>
          }
        />
        <div className="bg-card shadow sm:rounded-lg p-6 space-y-3 border border-border">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Square3Stack3DIcon />}
        title="Containers"
        description="Manage your stacks."
        actions={
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring cursor-pointer"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Stack
          </button>
        }
      />

      {error && <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">{error}</p></div>}

      {stacks.length === 0 ? (
          <div className="bg-card shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-foreground">No stacks</h3>
              <p className="mt-1 text-sm text-muted-foreground">Get started by creating your first container stack.</p>
              <div className="mt-6">
                <button
                  onClick={handleOpenCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Stack
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-card shadow sm:rounded-md">
            <div className="w-full overflow-x-auto overflow-y-visible touch-pan-x md:overflow-x-visible md:touch-auto">
            <table className="w-full min-w-[720px] divide-y divide-border">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" title="Month-to-date cost based on CPU + memory + uptime">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {stacks.map((stack) => {
                  const isExpanded = expandedStackId === stack.Id;
                  const tasks = stackTasks[stack.Id];
                  const isLoadingTasks = tasksLoading[stack.Id];
                  return (
                    <React.Fragment key={stack.Id}>
                      <tr className="hover:bg-muted/40 cursor-pointer" onClick={() => handleToggleTasks(stack)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                          <div className="flex min-w-0 items-center gap-2">
                            {isExpanded ? <ChevronDownIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRightIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                            <span className="truncate">{stack.Name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {stack.type === 'website' ? 'Website' : 'Custom'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground tabular-nums">
                          {formatCurrencyPrecise(stack.price_mtd ?? 0, stack.price_currency || 'EUR')}
                          <span className="ml-1 text-xs text-muted-foreground">/mo</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${stack.Status === 1 ? 'bg-success/10 text-success ring-success/20' : 'bg-destructive/10 text-destructive ring-destructive/20'}`}>
                            {stack.Status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            disabled={stack.type === 'website' || stack.Status !== 1}
                            onClick={() => handleRestartStack(stack.Id)}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                            title="Restart (redeploy)"
                          >
                            <ArrowPathIcon className="h-5 w-5" />
                          </button>
                          <Dropdown
                            align="right"
                            trigger={
                              <button
                                type="button"
                                className="p-1.5 rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                                title="Actions"
                              >
                                <EllipsisVerticalIcon className="h-5 w-5" />
                              </button>
                            }
                            items={[
                              {
                                label: 'Details',
                                icon: <InformationCircleIcon />,
                                onSelect: () => handleOpenDetails(stack),
                              },
                              {
                                label: stack.type === 'website' ? 'View' : 'Edit',
                                icon: stack.type === 'website' ? <EyeIcon /> : <PencilSquareIcon />,
                                onSelect: () => handleOpenEdit(stack),
                              },
                              stack.Status === 2
                                ? { label: 'Start', icon: <PlayIcon />, disabled: stack.type === 'website', onSelect: () => handleStartStack(stack.Id) }
                                : { label: 'Stop', icon: <StopIcon />, disabled: stack.type === 'website', onSelect: () => handleStopStack(stack.Id) },
                              ...(stack.type !== 'website'
                                ? [{ separator: true }, { label: 'Delete', icon: <TrashIcon />, danger: true, onSelect: () => handleDeleteStack(stack) }]
                                : []
                              ),
                            ]}
                          />
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-muted/30">
                          <td colSpan={5} className="px-6 py-4">
                            {isLoadingTasks && !tasks ? (
                              // Only show the spinner on the initial fetch — silent refreshes
                              // (the 2s poll while a task is transitioning) keep the existing
                              // table on screen so it doesn't flicker.
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                                Loading services...
                              </div>
                            ) : !tasks || tasks.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic">No tasks found for this stack.</p>
                            ) : (
                              <div className="w-full overflow-x-auto touch-pan-x">
                              <table className="w-full min-w-[980px] divide-y divide-border rounded-md overflow-hidden border border-border">
                                <thead className="bg-muted">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Task ID</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Image</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Slot</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Node</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Updated</th>
                                    <th className="px-4 py-2"></th>
                                  </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border">
                                  {tasks.map((task) => {
                                    const state = task.Status?.State ?? 'unknown';
                                    const isRunning = state === 'running';
                                    const isFailed = state === 'failed' || state === 'rejected' || state === 'shutdown';
                                    const isTransitional = !isRunning && !isFailed;
                                    const updatedAt = task.UpdatedAt ? formatDateTime(task.UpdatedAt) : '-';
                                    const image = (task.Spec?.ContainerSpec?.Image ?? '-').replace(/@sha256:[a-f0-9]+$/, '');
                                    return (
                                      <tr key={task.ID}>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${isRunning ? 'bg-success/10 text-success ring-success/20' : isFailed ? 'bg-destructive/10 text-destructive ring-destructive/20' : 'bg-warning/10 text-warning ring-warning/20'}`}>
                                            {isTransitional && <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning animate-pulse" />}
                                            {state.charAt(0).toUpperCase() + state.slice(1)}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-foreground">{task.ID}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-muted-foreground">{image}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-muted-foreground">{task.Slot ?? '-'}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-muted-foreground font-mono">{task.NodeID ? task.NodeID.substring(0, 12) : '-'}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-muted-foreground">{updatedAt}</td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                          {task.Status?.ContainerStatus?.ContainerID && (
                                            <div className="flex items-center gap-1">
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handleOpenLogs(stack.Id, task.Status.ContainerStatus!.ContainerID!); }}
                                                className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                                title="View logs"
                                              >
                                                <CommandLineIcon className="h-4 w-4" />
                                              </button>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handleOpenStats(stack.Id, task.Status.ContainerStatus!.ContainerID!); }}
                                                className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                                title="View stats"
                                              >
                                                <ChartBarIcon className="h-4 w-4" />
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}

      {/* Public API token + usage examples — token-authenticated start/stop/restart */}
      <StackApiSection
        apiToken={apiToken}
        revealed={tokenRevealed}
        onToggleReveal={() => setTokenRevealed((v) => !v)}
        onCopy={handleCopyApiToken}
        onRegenerate={handleRegenerateApiToken}
        regenerating={regeneratingToken}
        codeLang={apiCodeLang}
        onLangChange={setApiCodeLang}
        sampleStackName={(stacks.find((s) => s.type !== 'website')?.Name) || '<your-stack-name>'}
      />

      {detailsOpen && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[70]" onClick={() => setDetailsOpen(false)}>
          <div className="bg-card rounded-lg w-full max-w-3xl mx-4 flex flex-col border border-border shadow-xl" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-foreground">Stack details</span>
                {details && <span className="text-xs font-mono text-muted-foreground truncate max-w-xs">{details.stack_name}</span>}
              </div>
              <button onClick={() => setDetailsOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors" title="Close">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              {detailsLoading && (
                <div className="space-y-3">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              )}
              {detailsError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{detailsError}</div>
              )}
              {details && !detailsLoading && (
                <>
                  {/* Price card — the headline answer to "what does this stack cost me" */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">This month</div>
                        <div className="text-3xl font-bold text-foreground tabular-nums">
                          {formatCurrencyPrecise(details.price_mtd.base + details.price_mtd.cpu + details.price_mtd.mem, details.currency)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lifetime</div>
                        <div className="text-xl font-semibold text-foreground tabular-nums">
                          {formatCurrencyPrecise(details.price_lifetime.base + details.price_lifetime.cpu + details.price_lifetime.mem, details.currency)}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded bg-card px-2 py-1.5 border border-border">
                        <div className="text-muted-foreground">Base</div>
                        <div className="font-semibold tabular-nums">{formatCurrencyPrecise(details.price_mtd.base, details.currency)}</div>
                      </div>
                      <div className="rounded bg-card px-2 py-1.5 border border-border">
                        <div className="text-muted-foreground">CPU</div>
                        <div className="font-semibold tabular-nums">{formatCurrencyPrecise(details.price_mtd.cpu, details.currency)}</div>
                      </div>
                      <div className="rounded bg-card px-2 py-1.5 border border-border">
                        <div className="text-muted-foreground">Memory</div>
                        <div className="font-semibold tabular-nums">{formatCurrencyPrecise(details.price_mtd.mem, details.currency)}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      Rates: {formatCurrencyPrecise(details.rates.BasePerHour, details.currency)}/h base + {formatCurrencyPrecise(details.rates.CPUPerVCPUHour, details.currency)}/vCPU·h + {formatCurrencyPrecise(details.rates.MemPerGBHour, details.currency)}/GB·h
                    </div>
                  </div>

                  {/* Composition — service / network / volume counts straight from the compose file */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Services</div>
                      <div className="text-2xl font-bold text-foreground">{details.service_count}</div>
                    </div>
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Networks</div>
                      <div className="text-2xl font-bold text-foreground">{details.network_count}</div>
                    </div>
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Volumes</div>
                      <div className="text-2xl font-bold text-foreground">{details.volume_count}</div>
                    </div>
                  </div>

                  {/* Usage — averaged over the last 7 days of samples */}
                  <div className="rounded-lg border border-border">
                    <div className="px-3 py-2 border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Usage (last 7 days)
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-border">
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Avg CPU</div>
                        <div className="text-lg font-semibold tabular-nums">{details.avg_cpu_vcpu.toFixed(3)} vCPU</div>
                      </div>
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Peak CPU</div>
                        <div className="text-lg font-semibold tabular-nums">{details.peak_cpu_vcpu.toFixed(3)} vCPU</div>
                      </div>
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Avg Memory</div>
                        <div className="text-lg font-semibold tabular-nums">{formatBytes(details.avg_mem_bytes)}</div>
                      </div>
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Peak Memory</div>
                        <div className="text-lg font-semibold tabular-nums">{formatBytes(details.peak_mem_bytes)}</div>
                      </div>
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Total uptime</div>
                        <div className="text-lg font-semibold tabular-nums">{formatDuration(details.uptime_seconds)}</div>
                      </div>
                      <div className="bg-card p-3">
                        <div className="text-xs text-muted-foreground">Uptime % (7d)</div>
                        <div className="text-lg font-semibold tabular-nums">{details.uptime_percent.toFixed(1)}%</div>
                      </div>
                    </div>
                    {details.samples.length > 1 && (
                      <div className="px-3 pt-2 pb-3 grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">CPU trend</div>
                          <Sparkline
                            data={details.samples.map((s) => s.cpu_vcpu)}
                            color="#60a5fa"
                            fillColor="rgba(96,165,250,0.15)"
                            label=""
                          />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Memory trend</div>
                          <Sparkline
                            data={details.samples.map((s) => s.mem_bytes)}
                            color="#17B685"
                            fillColor="rgba(23,182,133,0.15)"
                            label=""
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lifecycle metadata */}
                  <div className="rounded-lg border border-border divide-y divide-border text-sm">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium capitalize">{details.type}</span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">Status</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${details.status === 1 ? 'bg-success/10 text-success ring-success/20' : 'bg-destructive/10 text-destructive ring-destructive/20'}`}>
                        {details.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">Containers (last sample)</span>
                      <span className="font-medium tabular-nums">{details.container_count}</span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">Created</span>
                      <span className="font-medium">{formatDateTime(details.created_at)}</span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">Last updated</span>
                      <span className="font-medium">{formatDateTime(details.updated_at)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {statsOpen && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[70]">
          <div className="bg-gray-900 rounded-lg w-full max-w-5xl mx-4 flex flex-col" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-5 w-5 text-white" />
                <span className="text-sm font-semibold text-gray-200">Container Stats</span>
                {statsName && <span className="text-xs font-mono text-muted-foreground truncate max-w-xs">{statsName}</span>}
                <span className="text-xs font-mono text-muted-foreground">{statsContainerId.substring(0, 12)}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`h-1.5 w-1.5 rounded-full ${statsLoading ? 'bg-warning animate-pulse' : 'bg-success'}`} />
                  {statsLoading ? 'Fetching…' : 'Live (5s)'}
                </span>
                <button onClick={() => setStatsOpen(false)} className="text-muted-foreground hover:text-white transition-colors" title="Close">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            {/* Stats grid */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* CPU */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">CPU Usage</span>
                  {statsHistory.length > 0 && (
                    <span className="text-2xl font-bold text-blue-400">{statsHistory[statsHistory.length - 1].cpuPercent.toFixed(2)}%</span>
                  )}
                </div>
                <Sparkline data={statsHistory.map(p => p.cpuPercent)} color="#60a5fa" fillColor="rgba(96,165,250,0.15)" label="Collecting CPU data…" />
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-block h-2 w-4 rounded" style={{ background: '#60a5fa' }} />
                  <span className="text-xs text-muted-foreground">CPU</span>
                </div>
              </div>
              {/* Memory */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Memory Usage</span>
                  {statsHistory.length > 0 && (
                    <span className="text-2xl font-bold text-success">{statsHistory[statsHistory.length - 1].memUsageMB.toFixed(1)} MB</span>
                  )}
                </div>
                <Sparkline data={statsHistory.map(p => p.memUsageMB)} color="#17B685" fillColor="rgba(23,182,133,0.15)" label="Collecting memory data…" />
                <div className="flex items-baseline justify-between mt-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-4 rounded" style={{ background: '#17B685' }} />
                    <span className="text-xs text-muted-foreground">Memory</span>
                  </div>
                  {statsHistory.length > 0 && (
                    <span className="text-xs text-muted-foreground">{statsHistory[statsHistory.length - 1].memPercent.toFixed(1)}% of limit</span>
                  )}
                </div>
              </div>
              {/* Network */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Network Usage</span>
                  {statsHistory.length > 0 && (
                    <div className="text-right">
                      <div className="text-sm font-bold text-blue-300">RX {fmtBytes(statsHistory[statsHistory.length - 1].netRxBytes)}</div>
                      <div className="text-sm font-bold text-orange-300">TX {fmtBytes(statsHistory[statsHistory.length - 1].netTxBytes)}</div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Sparkline data={statsHistory.map(p => p.netRxBytes)} color="#93c5fd" fillColor="rgba(147,197,253,0.12)" label="Collecting network data…" />
                  <div className="absolute inset-0 pointer-events-none">
                    <Sparkline data={statsHistory.map(p => p.netTxBytes)} color="#fb923c" fillColor="rgba(251,146,60,0.08)" />
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-4 rounded" style={{ background: '#93c5fd' }} />
                    <span className="text-xs text-muted-foreground">RX</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-4 rounded" style={{ background: '#fb923c' }} />
                    <span className="text-xs text-muted-foreground">TX</span>
                  </div>
                </div>
              </div>
              {/* I/O */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">I/O Usage</span>
                  {statsHistory.length > 0 && (
                    <div className="text-right">
                      <div className="text-sm font-bold text-success">Read {fmtBytes(statsHistory[statsHistory.length - 1].ioReadBytes)}</div>
                      <div className="text-sm font-bold text-rose-300">Write {fmtBytes(statsHistory[statsHistory.length - 1].ioWriteBytes)}</div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Sparkline data={statsHistory.map(p => p.ioReadBytes)} color="#17B685" fillColor="rgba(23,182,133,0.12)" label="Collecting I/O data…" />
                  <div className="absolute inset-0 pointer-events-none">
                    <Sparkline data={statsHistory.map(p => p.ioWriteBytes)} color="#fda4af" fillColor="rgba(253,164,175,0.08)" />
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-4 rounded" style={{ background: '#17B685' }} />
                    <span className="text-xs text-muted-foreground">Read</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-4 rounded" style={{ background: '#fda4af' }} />
                    <span className="text-xs text-muted-foreground">Write</span>
                  </div>
                </div>
              </div>
            </div>
            {/* PIDs footer */}
            {statsHistory.length > 0 && (
              <div className="px-4 pb-4">
                <div className="bg-gray-800 rounded-lg px-4 py-2 flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">PIDs</span>
                  <span className="text-sm font-bold text-gray-200">{statsHistory[statsHistory.length - 1].pids}</span>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {logsOpen && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[70]">
          <div className="bg-gray-900 rounded-lg w-full max-w-5xl mx-4 flex flex-col" style={{ height: '82vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <CommandLineIcon className="h-5 w-5 text-white" />
                <span className="text-sm font-semibold text-gray-200">Container Logs</span>
                <span className="text-xs font-mono text-muted-foreground">{logsContainerId.substring(0, 12)}</span>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={logsTail}
                  onChange={(e) => setLogsTail(e.target.value)}
                  className="text-xs bg-gray-800 text-gray-300 border border-gray-600 rounded px-2 py-1 focus:outline-none"
                >
                  <option value="50">50 lines</option>
                  <option value="100">100 lines</option>
                  <option value="500">500 lines</option>
                  <option value="1000">1000 lines</option>
                  <option value="all">All logs</option>
                </select>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`h-1.5 w-1.5 rounded-full ${logsLoading ? 'bg-warning animate-pulse' : 'bg-success'}`} />
                  {logsLoading ? 'Fetching...' : 'Live (5s)'}
                </span>
                <button onClick={() => setLogsOpen(false)} className="text-muted-foreground hover:text-white transition-colors" title="Close">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            {/* Logs area */}
            <pre
              ref={logsScrollRef}
              className="flex-1 overflow-auto px-4 py-3 text-xs font-mono text-success leading-relaxed"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {logsContent || (logsLoading ? 'Loading logs...' : 'No logs available.')}
            </pre>
          </div>
        </div>,
        document.body
      )}

      {showProgressModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-lg font-semibold text-foreground mb-5">{progressTitle}</h2>
            <div className="space-y-4">
              {progressSteps.map((step, idx) => {
                const isActive = idx === progressCurrentStep;
                const liveMs = isActive ? Date.now() - progressStepStartRef.current : 0;
                void liveTick; // triggers re-render for live counter
                return (
                  <div key={step.id} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      {step.status === 'completed' ? (
                        <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : step.status === 'error' ? (
                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      ) : isActive ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-border" />
                      )}
                    </div>
                    <div className="flex-1 flex items-baseline gap-2">
                      <span className={`text-sm text-foreground ${isActive ? 'font-medium' : ''}`}>{step.label}</span>
                      {isActive && (
                        <span className="text-muted-foreground text-xs tabular-nums">{(liveMs / 1000).toFixed(1)}s</span>
                      )}
                      {step.status === 'completed' && step.elapsed != null && (
                        <span className="text-muted-foreground text-xs tabular-nums">{(step.elapsed / 1000).toFixed(1)}s</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => setShowModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-foreground">
                        {modalMode === 'create' ? 'Create New Stack' : modalMode === 'edit' ? 'Edit Stack' : 'View Stack'}
                        {isReadOnly && <span className="ml-2 text-sm text-muted-foreground">(Read-only)</span>}
                      </h3>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <span className="text-sm text-muted-foreground font-medium">Edit as YAML</span>
                        <div
                          onClick={() => {
                            setShowYaml((v) => {
                              const next = !v;
                              if (next && !yamlDirty) {
                                // Seed textarea from current structured form on open.
                                setYamlDraft(generateYaml(stackForm));
                              }
                              return next;
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            showYaml ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${
                              showYaml ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </div>
                      </label>
                    </div>
                    {formError && (
                      <div className="mb-4 bg-destructive/10 border border-red-200 text-red-700 px-4 py-3 rounded">{formError}</div>
                    )}
                    {showYaml ? (
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <p className="text-xs text-muted-foreground">
                            {isReadOnly
                              ? 'Read-only YAML preview.'
                              : 'Edit the compose YAML directly. The server sandboxes the result: bind mounts, host-escape options, and external networks (other than traefik-public) are rejected. CPU/RAM limits and a per-volume size marker are auto-injected.'}
                          </p>
                          {!isReadOnly && yamlDirty && (
                            <button
                              type="button"
                              onClick={() => { setYamlDraft(generateYaml(stackForm)); setYamlDirty(false); }}
                              className="text-xs text-muted-foreground hover:text-foreground underline shrink-0"
                            >
                              Reset from form
                            </button>
                          )}
                        </div>
                        {isReadOnly ? (
                          <pre className="bg-gray-900 text-success rounded-lg p-4 text-xs overflow-auto" style={{ maxHeight: '60vh', fontFamily: 'monospace', whiteSpace: 'pre' }}>
                            {generateYaml(stackForm)}
                          </pre>
                        ) : (
                          <textarea
                            value={yamlDraft}
                            onChange={(e) => { setYamlDraft(e.target.value); setYamlDirty(true); }}
                            spellCheck={false}
                            className="bg-gray-900 text-success rounded-lg p-4 text-xs w-full font-mono"
                            style={{ minHeight: '50vh', maxHeight: '60vh', whiteSpace: 'pre', fontFamily: 'monospace' }}
                          />
                        )}
                      </div>
                    ) : (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground">Stack Name</label>
                        <input
                          type="text"
                          required
                          maxLength={25}
                          disabled={modalMode === 'edit' || isReadOnly}
                          value={stackForm.name}
                          onChange={(e) => setStackForm({ ...stackForm, name: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed"
                          placeholder="my-app"
                        />
                        <div className="mt-1 flex justify-between">
                          <p className="text-xs text-muted-foreground">Lowercase letters, numbers, hyphens. Max 25 chars.</p>
                          <p className={`text-xs font-medium ${stackForm.name.length >= 20 ? 'text-red-500' : 'text-muted-foreground'}`}>{stackForm.name.length}/25</p>
                        </div>
                      </div>

                      {/* Services */}
                      <div className="border-t border-border pt-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium text-foreground">Services</h4>
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={addService}
                              className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/40"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Service
                            </button>
                          )}
                        </div>
                        {stackForm.services.map((service, si) => (
                          <div key={si} className="mb-6 p-4 border border-border rounded-lg bg-muted/30">
                            <div className="flex justify-between items-center mb-4">
                              <h5 className="text-sm font-semibold text-foreground">Service {si + 1}</h5>
                              {!isReadOnly && stackForm.services.length > 1 && (
                                <button type="button" onClick={() => removeService(si)} className="text-red-600 hover:text-destructive text-sm font-medium">
                                  Remove Service
                                </button>
                              )}
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-foreground">Service Name</label>
                                <input
                                  type="text"
                                  required
                                  maxLength={Math.max(1, 25 - stackForm.name.length)}
                                  disabled={isReadOnly}
                                  value={service.name}
                                  onChange={(e) => updateService(si, 'name', e.target.value)}
                                  className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed"
                                  placeholder="web"
                                />
                                {stackForm.name.length > 0 && (
                                  <p className={`mt-1 text-xs ${service.name.length >= Math.max(1, 25 - stackForm.name.length) - 2 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                                    Max {Math.max(1, 25 - stackForm.name.length)} chars ({service.name.length}/{Math.max(1, 25 - stackForm.name.length)})
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground">Docker Image</label>
                                <input
                                  type="text"
                                  required
                                  disabled={isReadOnly}
                                  value={service.image}
                                  onChange={(e) => updateService(si, 'image', e.target.value)}
                                  className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed"
                                  placeholder="nginx:alpine"
                                />
                              </div>

                              {/* Ports */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="block text-sm font-medium text-foreground">Ports</label>
                                  {!isReadOnly && (
                                    <button type="button" onClick={() => addPort(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Port</button>
                                  )}
                                </div>
                                {service.ports.map((port, pi) => (
                                  <div key={pi} className="flex space-x-2 mb-2">
                                    <input type="text" disabled={isReadOnly} value={port.host} onChange={(e) => updatePort(si, pi, 'host', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Host port (8080)" />
                                    <input type="text" disabled={isReadOnly} value={port.container} onChange={(e) => updatePort(si, pi, 'container', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Container port (80)" />
                                    {!isReadOnly && service.ports.length > 1 && (
                                      <button type="button" onClick={() => removePort(si, pi)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Environment Variables */}
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-2">Environment Variables</label>
                                {service.environment.map((env, ei) => (
                                  <div key={ei} data-env-row={`${si}-${ei}`} className="flex space-x-2 mb-2">
                                    <input type="text" data-env-field="key" disabled={isReadOnly} value={env.key} onChange={(e) => updateEnvironment(si, ei, 'key', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="KEY" />
                                    <input type="text" data-env-field="value" disabled={isReadOnly} value={env.value} onChange={(e) => updateEnvironment(si, ei, 'value', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="value" />
                                    {!isReadOnly && service.environment.length > 1 && (
                                      <button type="button" onClick={() => removeEnvironment(si, ei)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                    )}
                                  </div>
                                ))}
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    onClick={() => addEnvironment(si)}
                                    className="w-full flex items-center justify-center gap-2 mt-1 py-2 px-3 rounded-md border border-dashed border-border text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                                  >
                                    <span className="text-base leading-none">+</span> Add Variable
                                  </button>
                                )}
                              </div>

                              {/* Volumes */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="block text-sm font-medium text-foreground">Volumes</label>
                                  {!isReadOnly && (
                                    <button type="button" onClick={() => addVolume(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Volume</button>
                                  )}
                                </div>
                                {service.volumes.map((volume, vi) => (
                                  <div key={vi} className="flex space-x-2 mb-2">
                                    <input type="text" disabled={isReadOnly} value={volume.host} onChange={(e) => updateVolume(si, vi, 'host', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Host path or volume name" />
                                    <input type="text" disabled={isReadOnly} value={volume.container} onChange={(e) => updateVolume(si, vi, 'container', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Container path" />
                                    {!isReadOnly && service.volumes.length > 1 && (
                                      <button type="button" onClick={() => removeVolume(si, vi)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Networks */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="block text-sm font-medium text-foreground">Networks</label>
                                  {!isReadOnly && (
                                    <button type="button" onClick={() => addNetwork(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Network</button>
                                  )}
                                </div>
                                {service.networks.map((network, ni) => (
                                  <div key={ni} className="flex space-x-2 mb-2">
                                    <input type="text" disabled={isReadOnly} value={network} onChange={(e) => updateNetwork(si, ni, e.target.value)} className="block w-full border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Network name (e.g., traefik-public)" />
                                    {!isReadOnly && service.networks.length > 1 && (
                                      <button type="button" onClick={() => removeNetwork(si, ni)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Labels */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="block text-sm font-medium text-foreground">Labels</label>
                                  {!isReadOnly && (
                                    <button type="button" onClick={() => addLabel(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Label</button>
                                  )}
                                </div>
                                {service.labels.map((label, li) => (
                                  <div key={li} className="flex space-x-2 mb-2">
                                    <input type="text" disabled={isReadOnly} value={label.key} onChange={(e) => updateLabel(si, li, 'key', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Label key" />
                                    <input type="text" disabled={isReadOnly} value={label.value} onChange={(e) => updateLabel(si, li, 'value', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Label value" />
                                    {!isReadOnly && service.labels.length > 1 && (
                                      <button type="button" onClick={() => removeLabel(si, li)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Deploy Configuration */}
                              <div className="border-t border-border pt-4 mt-4">
                                <div className="flex justify-between items-center cursor-pointer mb-3" onClick={() => toggleSection(`deploy-${si}`)}>
                                  <h6 className="text-sm font-semibold text-foreground">Deploy Configuration</h6>
                                  <svg className={`h-5 w-5 transition-transform ${expandedSections[`deploy-${si}`] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                                {expandedSections[`deploy-${si}`] && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="block text-sm font-medium text-foreground">Mode</label>
                                      <select
                                        disabled={isReadOnly}
                                        value={service.deploy.mode}
                                        onChange={(e) => updateServiceDeploy(si, 'mode', e.target.value)}
                                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed"
                                      >
                                        <option value="replicated">Replicated</option>
                                        <option value="global">Global</option>
                                      </select>
                                    </div>
                                    {service.deploy.mode === 'replicated' && (
                                      <div>
                                        <label className="block text-sm font-medium text-foreground">Replicas</label>
                                        <input
                                          type="number"
                                          min="1"
                                          disabled={isReadOnly}
                                          value={service.deploy.replicas}
                                          onChange={(e) => updateServiceDeploy(si, 'replicas', e.target.value)}
                                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed"
                                        />
                                      </div>
                                    )}
                                    <div>
                                      <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-foreground">Placement Constraints</label>
                                        {!isReadOnly && (
                                          <button type="button" onClick={() => addPlacementConstraint(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Constraint</button>
                                        )}
                                      </div>
                                      {service.deploy.placementConstraints.map((constraint, ci) => (
                                        <div key={ci} className="flex space-x-2 mb-2">
                                          <input type="text" disabled={isReadOnly} value={constraint} onChange={(e) => updatePlacementConstraint(si, ci, e.target.value)} className="block w-full border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="node.role==worker" />
                                          {!isReadOnly && service.deploy.placementConstraints.length > 1 && (
                                            <button type="button" onClick={() => removePlacementConstraint(si, ci)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    <div>
                                      <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-foreground">Deploy Labels</label>
                                        {!isReadOnly && (
                                          <button type="button" onClick={() => addDeployLabel(si)} className="text-xs text-muted-foreground hover:text-foreground">+ Add Deploy Label</button>
                                        )}
                                      </div>
                                      {service.deploy.labels.map((label, li) => (
                                        <div key={li} className="flex space-x-2 mb-2">
                                          <input type="text" disabled={isReadOnly} value={label.key} onChange={(e) => updateDeployLabel(si, li, 'key', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Label key" />
                                          <input type="text" disabled={isReadOnly} value={label.value} onChange={(e) => updateDeployLabel(si, li, 'value', e.target.value)} className="block w-1/2 border border-border rounded-md shadow-sm py-2 px-3 sm:text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="Label value" />
                                          {!isReadOnly && service.deploy.labels.length > 1 && (
                                            <button type="button" onClick={() => removeDeployLabel(si, li)} className="inline-flex items-center px-3 py-2 border border-destructive/40 text-sm leading-4 font-medium rounded-md text-destructive bg-card hover:bg-destructive/10">x</button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Global Networks */}
                      <div className="border-t border-border pt-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium text-foreground">Global Networks</h4>
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={addGlobalNetwork}
                              className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/40"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Network
                            </button>
                          )}
                        </div>
                        {Object.keys(stackForm.globalNetworks).length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">No global networks configured</p>
                        ) : (
                          Object.entries(stackForm.globalNetworks).map(([networkName, networkConfig]) => (
                            <div key={networkName} className="p-3 border border-border rounded-lg bg-muted/30 mb-3">
                              <div className="flex justify-between items-center mb-2">
                                <h6 className="text-sm font-semibold text-foreground">{networkName}</h6>
                                {!isReadOnly && (
                                  <button type="button" onClick={() => removeGlobalNetwork(networkName)} className="text-red-600 hover:text-destructive text-xs font-medium">Remove</button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-foreground mb-1">Driver</label>
                                  <input type="text" disabled={isReadOnly} value={networkConfig.driver || 'overlay'} onChange={(e) => updateGlobalNetwork(networkName, 'driver', e.target.value)} className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="overlay" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-foreground mb-1">External</label>
                                  <select disabled={isReadOnly} value={networkConfig.external ? 'true' : 'false'} onChange={(e) => updateGlobalNetwork(networkName, 'external', e.target.value === 'true')} className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm disabled:bg-muted disabled:cursor-not-allowed">
                                    <option value="false">false</option>
                                    <option value="true">true</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Global Volumes */}
                      <div className="border-t border-border pt-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium text-foreground">Global Volumes</h4>
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={addGlobalVolume}
                              className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/40"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Volume
                            </button>
                          )}
                        </div>
                        {Object.keys(stackForm.globalVolumes).length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">No global volumes configured</p>
                        ) : (
                          Object.entries(stackForm.globalVolumes).map(([volumeName, volumeConfig]) => (
                            <div key={volumeName} className="p-3 border border-border rounded-lg bg-muted/30 mb-3">
                              <div className="flex justify-between items-center mb-2">
                                <h6 className="text-sm font-semibold text-foreground">{volumeName}</h6>
                                {!isReadOnly && (
                                  <button type="button" onClick={() => removeGlobalVolume(volumeName)} className="text-red-600 hover:text-destructive text-xs font-medium">Remove</button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-foreground mb-1">Driver</label>
                                  <input type="text" disabled={isReadOnly} value={volumeConfig.driver || 'local'} onChange={(e) => updateGlobalVolume(volumeName, 'driver', e.target.value)} className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm disabled:bg-muted disabled:cursor-not-allowed" placeholder="local" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-foreground mb-1">External</label>
                                  <select disabled={isReadOnly} value={volumeConfig.external ? 'true' : 'false'} onChange={(e) => updateGlobalVolume(volumeName, 'external', e.target.value === 'true')} className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm disabled:bg-muted disabled:cursor-not-allowed">
                                    <option value="false">false</option>
                                    <option value="true">true</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  {!isReadOnly && (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {submitting ? 'Saving...' : modalMode === 'edit' ? 'Update Stack' : 'Create Stack'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isReadOnly ? 'Close' : 'Cancel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Containers;

// ---------------------------------------------------------------------------
// StackApiSection — API token + usage example for the public stack-control
// endpoints. Mirrors the pattern in the messaging service.

type CodeLang = 'curl' | 'python' | 'javascript';

interface StackApiSectionProps {
  apiToken: string;
  revealed: boolean;
  onToggleReveal: () => void;
  onCopy: () => void;
  onRegenerate: () => void;
  regenerating: boolean;
  codeLang: CodeLang;
  onLangChange: (lang: CodeLang) => void;
  sampleStackName: string;
}

const StackApiSection: React.FC<StackApiSectionProps> = ({
  apiToken,
  revealed,
  onToggleReveal,
  onCopy,
  onRegenerate,
  regenerating,
  codeLang,
  onLangChange,
  sampleStackName,
}) => {
  const apiUrl = (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_URL) || API_URL;
  const tokenForExample = apiToken || '<your-api-token>';
  const stackForExample = sampleStackName;

  const codeExamples: Record<CodeLang, string> = {
    curl: `# Start, stop or restart any of your stacks via HTTP.
# Replace <action> with one of: start | stop | restart.

curl -X POST "${apiUrl}/stacks/${stackForExample}/start" \\
  -H "X-Api-Token: ${tokenForExample}"

curl -X POST "${apiUrl}/stacks/${stackForExample}/stop" \\
  -H "X-Api-Token: ${tokenForExample}"

curl -X POST "${apiUrl}/stacks/${stackForExample}/restart" \\
  -H "X-Api-Token: ${tokenForExample}"`,

    python: `import requests

API_URL = "${apiUrl}"
API_TOKEN = "${tokenForExample}"

def control_stack(stack_name: str, action: str) -> dict:
    """action ∈ {'start', 'stop', 'restart'}."""
    response = requests.post(
        f"{API_URL}/stacks/{stack_name}/{action}",
        headers={"X-Api-Token": API_TOKEN},
        timeout=120,
    )
    response.raise_for_status()
    return response.json()

# Examples
control_stack("${stackForExample}", "start")
control_stack("${stackForExample}", "stop")
control_stack("${stackForExample}", "restart")`,

    javascript: `const API_URL = "${apiUrl}";
const API_TOKEN = "${tokenForExample}";

async function controlStack(stackName, action) {
  // action ∈ "start" | "stop" | "restart"
  const response = await fetch(\`\${API_URL}/stacks/\${stackName}/\${action}\`, {
    method: "POST",
    headers: { "X-Api-Token": API_TOKEN },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || \`Failed to \${action} stack\`);
  }
  return response.json();
}

// Examples
await controlStack("${stackForExample}", "start");
await controlStack("${stackForExample}", "stop");
await controlStack("${stackForExample}", "restart");`,
  };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2">
      {/* Usage examples (left) */}
      <div className="bg-card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">API Usage Examples</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Use the <code className="bg-muted px-1 py-0.5 rounded text-xs">X-Api-Token</code> header to authenticate.
          </p>
        </div>
        <div className="p-6">
          <div className="flex gap-2 mb-4">
            {(['curl', 'python', 'javascript'] as CodeLang[]).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => onLangChange(lang)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  codeLang === lang ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted'
                }`}
              >
                {lang === 'javascript' ? 'JavaScript' : lang.charAt(0).toUpperCase() + lang.slice(1)}
              </button>
            ))}
          </div>
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-xs leading-relaxed">
            <code>{codeExamples[codeLang]}</code>
          </pre>
          <p className="mt-3 text-xs text-muted-foreground">
            Endpoint: <code className="bg-muted px-1 rounded">POST /stacks/&lt;name&gt;/&lt;action&gt;</code> · action ∈ <code className="bg-muted px-1 rounded">start</code>, <code className="bg-muted px-1 rounded">stop</code>, <code className="bg-muted px-1 rounded">restart</code>.
          </p>
        </div>
      </div>

      {/* Token card (right) */}
      <div className="bg-card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Your API Token</h2>
          <p className="mt-1 text-sm text-muted-foreground">Use this token to start, stop or restart your stacks programmatically.</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Token</label>
            <div className="flex gap-2">
              <input
                type={revealed ? 'text' : 'password'}
                readOnly
                value={apiToken}
                className="flex-1 rounded-md border border-border px-3 py-2 text-sm font-mono bg-muted/30 focus:outline-none"
              />
              <button
                type="button"
                onClick={onToggleReveal}
                disabled={!apiToken}
                title={revealed ? 'Hide token' : 'Show token'}
                className="px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 text-xs disabled:opacity-50"
              >
                {revealed ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={onCopy}
                disabled={!apiToken}
                title="Copy token"
                className="px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 text-xs disabled:opacity-50"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Treat this like a password — anyone with it can control your stacks.</p>
          </div>
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            className="w-full inline-flex justify-center items-center rounded-md border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {regenerating ? 'Regenerating…' : 'Regenerate Token'}
          </button>
        </div>
      </div>
    </div>
  );
};

